import { showAppAlert } from '../common/appDialog';
// components/AudioVisualToolsModal.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    generateImage,
    analyzeImageWithPrompt,
    analyzeVideoWithPrompt,
    transcribeAudio,
    generateSpeech,
} from '@common/services/geminiService';
import Loader from './Loader';
import ApiErrorDisplay from './ApiErrorDisplay';
import { ICONS } from './constants';
import type { ImageAnalysisResult, VideoAnalysisResult, AudioTranscriptionResult, TextToSpeechResult } from '@types';
// FIX: Changed to a default import to resolve 'no exported member' error.
import AnalysisResultDisplay from './AnalysisResultDisplay';

// Helper functions from Gemini documentation for audio processing
function decode(base64: string) {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

async function decodeAudioData(
  data: Uint8Array,
  ctx: AudioContext,
  sampleRate: number,
  numChannels: number,
): Promise<AudioBuffer> {
  const dataInt16 = new Int16Array(data.buffer);
  const frameCount = dataInt16.length / numChannels;
  const buffer = ctx.createBuffer(numChannels, frameCount, sampleRate);

  for (let channel = 0; channel < numChannels; channel++) {
    const channelData = buffer.getChannelData(channel);
    for (let i = 0; i < frameCount; i++) {
      channelData[i] = dataInt16[i * numChannels + channel] / 32768.0;
    }
  }
  return buffer;
}


const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve((reader.result as string).split(',')[1]);
        reader.onerror = error => reject(error);
    });
};

type AvToolView = 'analyze-image' | 'analyze-video' | 'transcribe-audio' | 'tts' | 'gen-image' | 'gen-video';

const AudioVisualToolsModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
    const [currentView, setCurrentView] = useState<AvToolView>('analyze-image');
    const [error, setError] = useState<string | null>(null);

    // State for Image Analysis
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePrompt, setImagePrompt] = useState('');
    const [isLoadingImageAnalysis, setIsLoadingImageAnalysis] = useState(false);
    const [imageAnalysisResult, setImageAnalysisResult] = useState<ImageAnalysisResult | null>(null);

    // State for Video Analysis
    const [videoFile, setVideoFile] = useState<File | null>(null);
    const [videoPrompt, setVideoPrompt] = useState('');
    const [isLoadingVideoAnalysis, setIsLoadingVideoAnalysis] = useState(false);
    const [videoAnalysisResult, setVideoAnalysisResult] = useState<VideoAnalysisResult | null>(null);

    // State for Audio Transcription
    const [isRecording, setIsRecording] = useState(false);
    const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const [isLoadingTranscription, setIsLoadingTranscription] = useState(false);
    const [transcriptionResult, setTranscriptionResult] = useState<AudioTranscriptionResult | null>(null);
    
    // State for Text-to-Speech
    const [ttsText, setTtsText] = useState('');
    const [isLoadingTts, setIsLoadingTts] = useState(false);
    const [ttsResult, setTtsResult] = useState<TextToSpeechResult | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);

    // State for Image Generation
    const [genImagePrompt, setGenImagePrompt] = useState('');
    const [genImageResult, setGenImageResult] = useState('');
    const [isLoadingGenImage, setIsLoadingGenImage] = useState(false);
    const [videoGenerationResult, setVideoGenerationResult] = useState<string | null>(null);
    const [isLoadingVideoGeneration, setIsLoadingVideoGeneration] = useState(false);
    const [genImageAspectRatio, setGenImageAspectRatio] = useState('16:9');
    
    const handleAnalyzeImage = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!imageFile || !imagePrompt.trim()) return;
        setIsLoadingImageAnalysis(true);
        setImageAnalysisResult(null);
        setError(null);
        try {
            const base64Image = await fileToBase64(imageFile);
            const analysis = await analyzeImageWithPrompt(base64Image, imageFile.type, imagePrompt);
            setImageAnalysisResult({
                reportType: 'ImageAnalysisResult',
                imageUrl: URL.createObjectURL(imageFile),
                prompt: imagePrompt,
                analysis,
            });
        } catch (err: any) { setError(err.message); } 
        finally { setIsLoadingImageAnalysis(false); }
    };

    const handleAnalyzeVideo = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!videoFile || !videoPrompt.trim()) return;
        setIsLoadingVideoAnalysis(true);
        setVideoAnalysisResult(null);
        setError(null);
        try {
            const analysis = await analyzeVideoWithPrompt(videoFile, videoPrompt);
            setVideoAnalysisResult({
                reportType: 'VideoAnalysisResult',
                videoUrl: URL.createObjectURL(videoFile),
                prompt: videoPrompt,
                analysis,
            });
        } catch (err: any) { setError(err.message); }
        finally { setIsLoadingVideoAnalysis(false); }
    };
    
    const handleToggleRecording = async () => {
        if (isRecording) {
            mediaRecorderRef.current?.stop();
            setIsRecording(false);
        } else {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            const chunks: Blob[] = [];
            mediaRecorder.ondataavailable = (event) => chunks.push(event.data);
            mediaRecorder.onstop = async () => {
                const blob = new Blob(chunks, { type: 'audio/webm' });
                setAudioBlob(blob);
                // Automatically start transcription
                setIsLoadingTranscription(true);
                setTranscriptionResult(null);
                setError(null);
                try {
                    const transcription = await transcribeAudio(blob);
                    setTranscriptionResult({
                        reportType: 'AudioTranscriptionResult',
                        audioUrl: URL.createObjectURL(blob),
                        transcription,
                    });
                } catch (err: any) { setError(err.message); }
                finally { setIsLoadingTranscription(false); }
            };
            mediaRecorder.start();
            setIsRecording(true);
        }
    };
    
    const handleGenerateSpeech = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!ttsText.trim()) return;
        setIsLoadingTts(true);
        setTtsResult(null);
        setError(null);
        try {
            const audioData = await generateSpeech(ttsText);
            if (!audioContextRef.current) {
                audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
            }
            const audioContext = audioContextRef.current;
            const decodedBytes = decode(audioData);
            const audioBuffer = await decodeAudioData(decodedBytes, audioContext, 24000, 1);
            const source = audioContext.createBufferSource();
            source.buffer = audioBuffer;
            source.connect(audioContext.destination);
            source.start();

            const blob = new Blob([decodedBytes], { type: 'audio/l16; rate=24000' });
            setTtsResult({
                reportType: 'TextToSpeechResult',
                text: ttsText,
                audioUrl: URL.createObjectURL(blob),
            });
        } catch (err: any) { setError(err.message); }
        finally { setIsLoadingTts(false); }
    };

    const handleGenerateImage = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!genImagePrompt.trim()) return;
        setIsLoadingGenImage(true);
        setGenImageResult('');
        setError(null);
        try {
            const result = await generateImage(genImagePrompt, genImageAspectRatio as any);
            setGenImageResult(result);
        } catch (err: any) { setError('Failed to generate image. Please try again.'); } 
        finally { setIsLoadingGenImage(false); }
    };
    const handleGenerateVideo = async (e: React.FormEvent) => {
        e.preventDefault();
        void showAppAlert("Video Generation not yet implemented.");
    };
        
    const TabButton: React.FC<{ viewId: AvToolView; label: string; icon: React.ReactNode }> = ({ viewId, label, icon }) => (
        <button
            onClick={() => setCurrentView(viewId)}
            className={`flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-md transition-colors ${currentView === viewId ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-200'}`}
        >
            {icon} {label}
        </button>
    );

    return (
        <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-slate-50 rounded-xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col">
                <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-white rounded-t-xl">
                    <h2 className="text-xl font-bold text-slate-800">Audio Visual Tools</h2>
                    <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700 rounded-full">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>

                <div className="p-2 border-b border-slate-200 bg-white flex items-center gap-1 flex-wrap">
                    <TabButton viewId="analyze-image" label="Image Analysis" icon={ICONS.image} />
                    <TabButton viewId="analyze-video" label="Video Analysis" icon={ICONS.video} />
                    <TabButton viewId="transcribe-audio" label="Audio Transcription" icon={ICONS.audio} />
                    <TabButton viewId="tts" label="Text-to-Speech" icon={ICONS.tts} />
                    <TabButton viewId="gen-image" label="Image Generation" icon={ICONS.image_generation} />
                    <TabButton viewId="gen-video" label="Video Generation" icon={ICONS.video_generation} />
                </div>
                
                <div className="flex-1 p-6 overflow-y-auto">
                    <ApiErrorDisplay error={error} serviceName="Audio Visual Tools" />
                    {currentView === 'analyze-image' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-full">
                           <form onSubmit={handleAnalyzeImage} className="space-y-4">
                                <h3 className="text-lg font-semibold text-slate-700">Image Analysis</h3>
                                <input type="file" accept="image/*" onChange={e => setImageFile(e.target.files ? e.target.files[0] : null)} className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"/>
                                <textarea value={imagePrompt} onChange={e => setImagePrompt(e.target.value)} placeholder="Prompt (e.g., 'Identify security vulnerabilities in this image.')" className="w-full p-2 border rounded-md" rows={4} />
                                <button type="submit" disabled={isLoadingImageAnalysis || !imageFile || !imagePrompt} className="w-full bg-blue-600 text-white p-2 rounded-md disabled:bg-slate-400">Analyze Image</button>
                            </form>
                             <div className="bg-white rounded-lg border border-slate-200 p-4 overflow-y-auto">
                                {isLoadingImageAnalysis && <Loader />}
                                {imageAnalysisResult && <AnalysisResultDisplay result={imageAnalysisResult} />}
                            </div>
                        </div>
                    )}
                     {currentView === 'analyze-video' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-full">
                           <form onSubmit={handleAnalyzeVideo} className="space-y-4">
                                <h3 className="text-lg font-semibold text-slate-700">Video Analysis</h3>
                                <input type="file" accept="video/*" onChange={e => setVideoFile(e.target.files ? e.target.files[0] : null)} className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"/>
                                <textarea value={videoPrompt} onChange={e => setVideoPrompt(e.target.value)} placeholder="Prompt (e.g., 'Summarize the key events in this video.')" className="w-full p-2 border rounded-md" rows={4} />
                                <button type="submit" disabled={isLoadingVideoAnalysis || !videoFile || !videoPrompt} className="w-full bg-blue-600 text-white p-2 rounded-md disabled:bg-slate-400">Analyze Video</button>
                            </form>
                             <div className="bg-white rounded-lg border border-slate-200 p-4 overflow-y-auto">
                                {isLoadingVideoAnalysis && <Loader message="Analyzing video frames... This may take a moment." />}
                                {videoAnalysisResult && <AnalysisResultDisplay result={videoAnalysisResult} />}
                            </div>
                        </div>
                    )}
                     {currentView === 'transcribe-audio' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-full">
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold text-slate-700">Audio Transcription</h3>
                                <button onClick={handleToggleRecording} className={`w-full p-4 rounded-md font-bold text-white flex items-center justify-center gap-2 ${isRecording ? 'bg-red-600' : 'bg-blue-600'}`}>
                                    {isRecording ? 'Stop Recording' : 'Start Recording'}
                                </button>
                                {isRecording && <p className="text-sm text-center text-red-500 animate-pulse">Recording in progress...</p>}
                            </div>
                             <div className="bg-white rounded-lg border border-slate-200 p-4 overflow-y-auto">
                                {isLoadingTranscription && <Loader message="Transcribing audio..." />}
                                {transcriptionResult && <>
                                    <h4 className="font-semibold mb-2">Transcription:</h4>
                                    <p className="p-2 bg-slate-100 rounded-md text-sm">{transcriptionResult.transcription}</p>
                                    <h4 className="font-semibold mt-4 mb-2">Playback:</h4>
                                    <audio src={transcriptionResult.audioUrl} controls className="w-full" />
                                </>}
                            </div>
                        </div>
                    )}
                     {currentView === 'tts' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-full">
                           <form onSubmit={handleGenerateSpeech} className="space-y-4">
                                <h3 className="text-lg font-semibold text-slate-700">Text-to-Speech</h3>
                                <textarea value={ttsText} onChange={e => setTtsText(e.target.value)} placeholder="Enter text to convert to speech..." className="w-full p-2 border rounded-md" rows={6} />
                                <button type="submit" disabled={isLoadingTts || !ttsText} className="w-full bg-blue-600 text-white p-2 rounded-md disabled:bg-slate-400">Generate Speech</button>
                            </form>
                             <div className="bg-white rounded-lg border border-slate-200 p-4 overflow-y-auto">
                                {isLoadingTts && <Loader message="Generating speech..." />}
                                {ttsResult && <>
                                    <h4 className="font-semibold mb-2">Generated Audio:</h4>
                                    <audio src={ttsResult.audioUrl} controls autoPlay className="w-full" />
                                    <h4 className="font-semibold mt-4 mb-2">Original Text:</h4>
                                    <p className="p-2 bg-slate-100 rounded-md text-sm">{ttsResult.text}</p>
                                </>}
                            </div>
                        </div>
                    )}
                    {currentView === 'gen-image' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-full">
                            <form onSubmit={handleGenerateImage} className="space-y-4">
                                <h3 className="text-lg font-semibold text-slate-700">Image Generation</h3>
                                <textarea value={genImagePrompt} onChange={e => setGenImagePrompt(e.target.value)} placeholder="A photorealistic image of a security operations center..." className="w-full p-2 border rounded-md" rows={5}/>
                                <select value={genImageAspectRatio} onChange={e => setGenImageAspectRatio(e.target.value)} className="w-full p-2 border rounded-md">
                                    <option value="16:9">16:9 (Widescreen)</option>
                                    <option value="1:1">1:1 (Square)</option>
                                    <option value="9:16">9:16 (Vertical)</option>
                                    <option value="4:3">4:3 (Standard)</option>
                                    <option value="3:4">3:4 (Portrait)</option>
                                </select>
                                <button type="submit" disabled={isLoadingGenImage} className="w-full bg-blue-600 text-white p-2 rounded-md disabled:bg-slate-400">Generate Image</button>
                            </form>
                            <div className="bg-white rounded-lg border border-slate-200 p-4 flex flex-col items-center justify-center">
                                {isLoadingGenImage ? <Loader /> : genImageResult ? <>
                                    <img src={genImageResult} alt="Generated" className="max-w-full max-h-[calc(100%-4rem)] rounded-md object-contain" />
                                    <a href={genImageResult} download="inteloptics-generated-image.jpg" className="mt-4 bg-blue-100 text-blue-700 text-sm font-semibold py-2 px-4 rounded-md">Download</a>
                                </> : <p className="text-slate-500">Image will appear here.</p>}
                            </div>
                        </div>
                    )}
                    {currentView === 'gen-video' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-full">
                           <form onSubmit={handleGenerateVideo} className="space-y-4">
                                <h3 className="text-lg font-semibold text-slate-700">Video Generation</h3>
                                <textarea value={videoPrompt} onChange={e => setVideoPrompt(e.target.value)} placeholder="A short, 3-second clip of a surveillance camera panning across a cityscape at night..." className="w-full p-2 border rounded-md" rows={5}/>
                                <button type="submit" disabled={isLoadingVideoGeneration} className="w-full bg-blue-600 text-white p-2 rounded-md disabled:bg-slate-400">Generate Video</button>
                            </form>
                            <div className="bg-white rounded-lg border border-slate-200 p-4 flex flex-col items-center justify-center">
                                {isLoadingVideoGeneration ? <Loader message="Generating video..."/> : videoGenerationResult ? <>
                                    <video src={videoGenerationResult} controls autoPlay loop className="max-w-full max-h-[calc(100%-4rem)] rounded-md" />
                                    <a href={videoGenerationResult} download="inteloptics-generated-video.mp4" className="mt-4 bg-blue-100 text-blue-700 text-sm font-semibold py-2 px-4 rounded-md">Download</a>
                                </> : <p className="text-slate-500">Video will appear here.</p>}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};


export default AudioVisualToolsModal;
